import { AlertTriangle, AndroidIcon, AppleIcon, Avatar, Check, HourglassIcon, Smartphone, X } from '@chain-zen/ui'
import React from 'react'

import { LocationInfoBadge } from '../../../components/shared/location-info-badge'
import { PhoneInfoBadge } from '../../../components/shared/phone-info-badge'
import {
  AuthorizationStatusEnum,
  DriverAppDownloadMessageStatusEnum,
  type IDeviceInfo,
  type IDriverAppDownloadMessageAction,
  type TLocationSettingsType
} from '../../../types/tracking'
import type { UserDetails } from '../../../types/user'
import { dateTimeAgo } from '../../../utils/datetime-utils'
import { formatLocationText } from '../../../utils/format-location-text'
import { formatPhoneNumber } from '../../../utils/phone'
import { formatDriverAppActionTime } from '../utils'
import { getMobileTrackingIssueLabels } from './device-info-utils'
import { isMoreThanEightHoursAgo } from './shared'

/**
 * The component that displays the device info and its tooltip
 */
export const DeviceInfo = (props: {
  deviceInfo: IDeviceInfo | null
  hasSentMockLocations?: boolean
  hasHistoricalDeviceMetadata: boolean
  isDeviceTracking: { isTracking: boolean; reason?: string }
  isHistoricalDevice: boolean
  lastSeen: string | null
  lastLocationCity: string | null
  lastLocationStateCode: string | null
  lastLocationTimestamp: string | null
  latestAction: IDriverAppDownloadMessageAction | null
  locationSettings: TLocationSettingsType | null
  isLoggedIn?: boolean
  isTrackingOn?: boolean
  phoneNumberInfo?: UserDetails['phoneNumberInfo']
  userName: string | null
  userPhone: string | null
  userPhoto: string | null
}) => {
  const {
    deviceInfo,
    hasSentMockLocations = false,
    hasHistoricalDeviceMetadata,
    isDeviceTracking,
    isHistoricalDevice,
    lastSeen,
    lastLocationCity,
    lastLocationStateCode,
    lastLocationTimestamp,
    locationSettings,
    isLoggedIn,
    isTrackingOn,
    phoneNumberInfo,
    userName,
    userPhone,
    userPhoto,
    latestAction
  } = props

  const lastSeenForDisplay = lastLocationTimestamp || lastSeen
  const formattedLastSeen = lastSeenForDisplay ? dateTimeAgo(lastSeenForDisplay) : null

  const incorrectAuthorization =
    locationSettings?.authorizationStatus === AuthorizationStatusEnum.AuthorizationStatusAlways ? false : true

  const isDeviceNotDetected = isMoreThanEightHoursAgo(lastSeen) && !incorrectAuthorization && !isHistoricalDevice

  const isTracking = isDeviceTracking?.isTracking

  const hasFormattedLastSeen = Boolean(formattedLastSeen)
  const lastLocationAgeMs = lastLocationTimestamp ? Date.now() - new Date(lastLocationTimestamp).getTime() : null
  const hasRecentLocation =
    typeof lastLocationAgeMs === 'number' && !Number.isNaN(lastLocationAgeMs) && lastLocationAgeMs >= 0
  const isFreshLocation = hasRecentLocation && lastLocationAgeMs <= 2 * 60 * 60 * 1000
  const locationText = formatLocationText(lastLocationCity || undefined, lastLocationStateCode || undefined)
  const hasLocationText = locationText !== '-'
  const issueLabels = getMobileTrackingIssueLabels({
    isDeviceNotDetected,
    isLoggedIn,
    isTrackingOn,
    locationSettings
  })
  const issueSummary = issueLabels.length > 0 ? issueLabels.join(' • ') : isDeviceTracking.reason || null

  const trackingIcon = isTracking && !isDeviceNotDetected ? Check : isDeviceNotDetected ? AlertTriangle : X

  const trackingIconColor =
    isTracking && !isDeviceNotDetected
      ? 'text-green-500 dark:text-green-400'
      : isDeviceNotDetected
        ? 'text-yellow-500 dark:text-yellow-400'
        : 'text-red-500 dark:text-red-400'

  // Build message for historical device status
  const actionTime = formatDriverAppActionTime(latestAction)
  const actionLabel = !latestAction
    ? 'Tracking request SMS pending'
    : `Tracking request SMS ${
        latestAction.status === DriverAppDownloadMessageStatusEnum.Scheduled
          ? 'scheduled to send'
          : latestAction.status === DriverAppDownloadMessageStatusEnum.Canceled
            ? 'cancelled'
            : 'sent'
      }${actionTime ? ` ${actionTime}` : ''}`

  const formattedUserPhone = userPhone ? formatPhoneNumber(userPhone) : null

  const deviceLabel = deviceInfo?.deviceModel
    ? `${deviceInfo.deviceModel} / ${userName || 'Unknown'}'s Phone`
    : `${userName || 'Unknown'}'s Phone`
  const normalizedDevicePlatform = deviceInfo?.devicePlatform?.toLowerCase() || ''
  const isIOSDevice =
    normalizedDevicePlatform.includes('ios') ||
    normalizedDevicePlatform.includes('iphone') ||
    normalizedDevicePlatform.includes('apple')
  const isAndroidDevice = normalizedDevicePlatform.includes('android')

  return isHistoricalDevice ? (
    <div className="flex flex-col flex-1 min-w-0 gap-1">
      <div className="flex items-center gap-1 mb-0.5 flex-wrap">
        <HourglassIcon className="h-3 w-3 shrink-0 self-center text-yellow-500 dark:text-yellow-400" />
        <Smartphone className="h-3 w-3 shrink-0 self-center text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{actionLabel}</span>
        {!!formattedUserPhone ? (
          <>
            <span className="text-muted-foreground text-xs">•</span>
            <Avatar size="2xs" name={userName || formattedUserPhone} src={userPhoto || ''} />
            <span className="text-xs text-muted-foreground">{formattedUserPhone}</span>
          </>
        ) : null}
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {!formattedLastSeen ? <span className="text-xs text-muted-foreground">Chain app not installed</span> : null}
          {hasFormattedLastSeen && !hasHistoricalDeviceMetadata ? (
            <>
              {!formattedLastSeen ? <span className="text-muted-foreground text-xs">•</span> : null}
              <span className="text-xs text-muted-foreground">Device information not available</span>
            </>
          ) : null}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {!!phoneNumberInfo && <PhoneInfoBadge phoneNumberInfo={phoneNumberInfo} showHealthy={false} />}
          {!!locationSettings && <LocationInfoBadge locationInfo={locationSettings} showHealthy={false} />}
          {!!hasSentMockLocations && (
            <span className="text-xs text-red-500 dark:text-red-400">Fake locations detected</span>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-1 mb-0.5 flex-wrap">
        <div className={`inline-flex h-4 w-4 shrink-0 items-center justify-center ${trackingIconColor}`}>
          {React.createElement(trackingIcon, { className: 'h-4 w-4' })}
        </div>
        {isIOSDevice ? (
          <AppleIcon
            size={12}
            className="shrink-0 self-center text-muted-foreground !rounded-none !border-0 !bg-transparent !p-0"
          />
        ) : null}
        {isAndroidDevice ? (
          <AndroidIcon size={12} className="shrink-0 self-center !rounded-none !border-0 !bg-transparent !p-0" />
        ) : null}
        {!isIOSDevice && !isAndroidDevice ? (
          <Smartphone className="h-4 w-4 shrink-0 self-center text-muted-foreground" />
        ) : null}
        <div className="min-w-0 flex items-center gap-1 flex-wrap">
          <span className="text-sm font-medium text-foreground break-words leading-5">{deviceLabel}</span>
          <Avatar size="2xs" name={userName || ''} src={userPhoto || ''} />
        </div>
        {hasFormattedLastSeen ? (
          <span className="inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            {hasLocationText ? (
              <span className="relative inline-flex h-2 w-2 flex-shrink-0">
                <span
                  className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${isFreshLocation ? 'bg-green-500/60 dark:bg-green-400/50' : 'bg-yellow-500/70 dark:bg-yellow-400/60'}`}
                />
                <span
                  className={`relative inline-flex h-2 w-2 rounded-full ${isFreshLocation ? 'bg-green-500 dark:bg-green-400' : 'bg-yellow-500 dark:bg-yellow-400'}`}
                />
              </span>
            ) : null}
            <span className="break-words">{`Last seen ${formattedLastSeen || ''}${hasLocationText ? ` in ${locationText}` : ''}`}</span>
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {!hasFormattedLastSeen ? <span className="text-xs text-muted-foreground">Chain app not installed</span> : null}
        {!!issueSummary ? (
          <>
            {!hasFormattedLastSeen ? <span className="text-muted-foreground text-xs">•</span> : null}
            <span className="text-xs text-muted-foreground" title={isDeviceTracking.reason || issueSummary}>
              {issueSummary}
            </span>
          </>
        ) : null}
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {!!locationSettings && <LocationInfoBadge locationInfo={locationSettings} showHealthy={false} />}
        {!!phoneNumberInfo && <PhoneInfoBadge phoneNumberInfo={phoneNumberInfo} showHealthy={false} />}
        {!!hasSentMockLocations && (
          <span className="text-xs text-red-500 dark:text-red-400">Fake locations detected</span>
        )}
      </div>
    </div>
  )
}
